import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const AgentCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "15px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15)",
  },
}));

const AgentsSection = ({ agentDetails }) => {
  return (
    <PageSection sx={{ bgcolor: "#fff" }}>
      <Container>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <SectionTitle variant="h4" component="h2">
            Meet Our Team
          </SectionTitle>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: "700px", mx: "auto" }}
          >
            At DESCO Real Estate, our strength lies in our people. We pride
            ourselves on a team of dedicated, experienced and client-centric
            agents who go above and beyond to deliver value.
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {agentDetails?.map((agent, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AgentCard
                  sx={{
                    borderRadius: 4,
                    height: "100%",
                    boxShadow: 3,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 4 }}>
                    <CardMedia
                      component="img"
                      sx={{
                        height: 140,
                        width: 140,
                        borderRadius: "50%",
                        mx: "auto",
                        mb: 3,
                        objectFit: "cover",
                      }}
                      image={agent.image}
                      alt={agent?.title}
                    />

                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                      sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
                    >
                      {agent.title}
                    </Typography>

                    {/* Decorative Line */}
                    <Box
                      sx={{
                        width: 40,
                        height: 3,
                        backgroundColor: "#C10E14",
                        mx: "auto",
                        my: 1.5,
                        borderRadius: 2,
                      }}
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        textAlign: "justify",
                        textJustify: "inter-word",
                        lineHeight: 1.6,
                        fontSize: "16px",
                        mt: 1,
                      }}
                    >
                      {agent?.description}
                    </Typography>
                  </CardContent>
                </AgentCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </PageSection>
  );
};

export default AgentsSection;
